import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Block, ExpandMore, Receipt } from "@mui/icons-material";
import dayjs from "dayjs";
import type { InvoiceByPeriodItem, InvoicesByPeriod } from "./types";

interface InvoicesByPeriodSectionProps {
  periods: InvoicesByPeriod[];
  onVoidInvoice?: (invoice: InvoiceByPeriodItem) => void;
  voidingInvoiceId?: number | null;
}

const isVoided = (invoice: InvoiceByPeriodItem) => Boolean(invoice.voided_at);

const formatInvoiceRef = (invoiceId: number): string => `INV-${invoiceId}`;

const invoiceStatus = (
  invoice: InvoiceByPeriodItem,
): { label: string; color: "success" | "warning" | "default" } => {
  if (isVoided(invoice)) return { label: "Voided", color: "default" };
  if (invoice.paid) return { label: "Paid", color: "success" };
  return { label: "Unpaid", color: "warning" };
};

const MemberAvatarBlock: React.FC<{
  invoice: InvoiceByPeriodItem;
  bg: string;
}> = ({ invoice, bg }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
    }}
  >
    <Avatar
      sx={{
        width: 28,
        height: 28,
        bgcolor: bg,
        fontSize: "0.75rem",
        flexShrink: 0,
      }}
    >
      {invoice.handler_name.charAt(0)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {invoice.handler_name}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {invoice.email_address}
      </Typography>
    </Box>
  </Box>
);

const InvoicesByPeriodSection: React.FC<InvoicesByPeriodSectionProps> = ({
  periods,
  onVoidInvoice,
  voidingInvoiceId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filter, setFilter] = useState<"recent" | "all">("recent");

  const cutoff = dayjs().subtract(28, "day");
  const filteredPeriods =
    filter === "recent"
      ? periods.filter((p) => dayjs(p.period_end).isAfter(cutoff))
      : periods;

  const renderStatusChip = (inv: InvoiceByPeriodItem) => {
    const status = invoiceStatus(inv);
    return (
      <Chip label={status.label} size="small" color={status.color} />
    );
  };

  const canVoid = (inv: InvoiceByPeriodItem) =>
    !inv.paid && !isVoided(inv) && onVoidInvoice != null;

  const renderVoidButton = (inv: InvoiceByPeriodItem) => {
    if (!canVoid(inv)) return null;
    const isVoiding = voidingInvoiceId === inv.id;
    return (
      <Button
        size="small"
        color="warning"
        variant="outlined"
        startIcon={<Block />}
        disabled={isVoiding || voidingInvoiceId != null}
        onClick={() => onVoidInvoice?.(inv)}
      >
        {isVoiding ? "Voiding…" : "Void"}
      </Button>
    );
  };

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          bgcolor: theme.palette.secondary.light + "15",
          borderRadius: 1,
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt />
          <Typography sx={{ fontWeight: 600 }}>
            Invoices by period ({filteredPeriods.length} period
            {filteredPeriods.length !== 1 ? "s" : ""})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => {
            if (v) setFilter(v);
          }}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="recent">Last 28 days</ToggleButton>
          <ToggleButton value="all">All time</ToggleButton>
        </ToggleButtonGroup>

        {filteredPeriods.length === 0 ? (
          <Alert severity="info">
            No invoices issued in the last 28 days.
          </Alert>
        ) : (
          <Stack spacing={{ xs: 2, sm: 3 }}>
            {filteredPeriods.map((period, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{ p: { xs: 1.25, sm: 2 } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    mb: 2,
                    pb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, width: { xs: "100%", sm: "auto" } }}
                  >
                    {period.group_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dayjs(period.period_start).format("DD/MM/YYYY")} –{" "}
                    {dayjs(period.period_end).format("DD/MM/YYYY")}
                  </Typography>
                  <Chip
                    label={`${period.paid_invoices}/${period.total_invoices} paid`}
                    size="small"
                    color={period.unpaid_invoices === 0 ? "success" : "warning"}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: { xs: 0.5, sm: 1.5 },
                      width: { xs: "100%", sm: "auto" },
                      "& > *": {
                        fontSize: "0.8125rem",
                      },
                    }}
                  >
                    <Typography variant="body2">
                      Total:{" "}
                      <strong>£{period.total_amount.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Paid: £{period.paid_amount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      Unpaid: £{period.unpaid_amount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {isMobile ? (
                  <Stack spacing={1}>
                    {period.invoices.map((inv) => (
                      <Box
                        key={inv.id}
                        sx={{
                          p: 1.25,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        <MemberAvatarBlock
                          invoice={inv}
                          bg={theme.palette.secondary.main}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatInvoiceRef(inv.id)}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            £{inv.amount.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Due {dayjs(inv.due_date).format("DD/MM/YYYY")}
                          </Typography>
                          {renderStatusChip(inv)}
                        </Box>
                        {renderVoidButton(inv)}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Due date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          {onVoidInvoice && (
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Actions
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {period.invoices.map((inv) => (
                          <TableRow key={inv.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatInvoiceRef(inv.id)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <MemberAvatarBlock
                                invoice={inv}
                                bg={theme.palette.secondary.main}
                              />
                            </TableCell>
                            <TableCell>£{inv.amount.toFixed(2)}</TableCell>
                            <TableCell>
                              {dayjs(inv.due_date).format("DD/MM/YYYY")}
                            </TableCell>
                            <TableCell>{renderStatusChip(inv)}</TableCell>
                            {onVoidInvoice && (
                              <TableCell align="right">
                                {renderVoidButton(inv)}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default InvoicesByPeriodSection;
